/**
 * Signature interaction tracking.
 *
 * Fires events to POST /api/v1/signatures/track using sendBeacon
 * for fire-and-forget delivery that survives page unloads.
 */

import { config } from './config';

interface TrackingEvent {
  tenantId: string;
  signatureTemplateId: string;
  eventType: 'impression' | 'click' | 'cta_click';
  elementId?: string;
  destinationUrl?: string;
  metadata?: Record<string, string>;
}

/** Track a signature impression (signature rendered in compose window). */
export function trackSignatureImpression(templateId: string, tenantId: string): void {
  sendTrackingEvent({
    tenantId,
    signatureTemplateId: templateId,
    eventType: 'impression',
    metadata: {
      source: 'outlook_addin',
      client: detectOutlookClient(),
    },
  });
}

/** Track a signature CTA / banner link click. */
export function trackSignatureClick(templateId: string, tenantId: string, url: string, elementId?: string): void {
  sendTrackingEvent({
    tenantId,
    signatureTemplateId: templateId,
    eventType: 'cta_click',
    elementId,
    destinationUrl: url,
    metadata: {
      source: 'outlook_addin',
      client: detectOutlookClient(),
    },
  });
}

function sendTrackingEvent(event: TrackingEvent): void {
  const url = `${config.apiBaseUrl}/signatures/track`;
  const body = JSON.stringify(event);

  try {
    // sendBeacon is fire-and-forget — ideal for tracking.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback for environments without sendBeacon.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {
        // Tracking is best-effort — don't block on failure.
      });
    }
  } catch {
    // Tracking failure should never break signature application.
  }
}

function detectOutlookClient(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (ua.includes('Outlook')) return 'outlook_desktop';
  if (ua.includes('Edge') || ua.includes('Chrome')) return 'outlook_web';
  return 'outlook_unknown';
}
